import * as Popover from "@radix-ui/react-popover";

import { Button } from "react-migration/components/Button";
import { Loading } from "react-migration/components/Loading";
import { ReactNode } from "react";
import { Theme } from "react-migration/lib/theme/Theme";
import { Tooltip } from "react-migration/components/Tooltip";
import cn from "clsx";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

export enum SaveButtonState {
  IDLE = "IDLE",
  LOADING = "LOADING",
  SAVED = "SAVED",
  ERROR = "ERROR",
}

interface SaveButtonProps {
  buttonState: SaveButtonState;
  copy?: {
    save?: ReactNode;
    saved?: ReactNode;
  };
  onSave: () => void;
  onExpand?: () => void;
  readonly fullWidth?: boolean;
  readonly expanded?: boolean;
}

export function SaveButton({ buttonState, copy, onSave, onExpand, fullWidth, expanded = false }: SaveButtonProps) {
  const { t } = useTranslation();

  const {
    save: saveCopy = t("sites.save_site.save_site_dropdown.save"),
    saved: savedCopy = t("sites.save_site.save_site_dropdown.saved"),
  } = copy || {};

  const isDisabled = [SaveButtonState.LOADING, SaveButtonState.SAVED, SaveButtonState.ERROR].includes(buttonState);
  const isExpandable = onExpand && ![SaveButtonState.SAVED, SaveButtonState.ERROR].includes(buttonState);

  return (
    <Tooltip.Wrapper>
      <Tooltip.Trigger>
        <div data-testid="tooltip-trigger" className="flex items-stretch">
          <Button
            onClick={onSave}
            disabled={isDisabled}
            data-testid="save-site-outer"
            size="md"
            className={cn({
              "w-full": fullWidth,
              "px-3! py-1.5!": expanded,
              "pr-1! md:pr-3!": !expanded,
              "px-2! pl-3! rounded-r-none!": isExpandable,
            })}
          >
            {buttonState === SaveButtonState.ERROR ? (
              saveCopy
            ) : buttonState === SaveButtonState.LOADING ? (
              <div className="flex items-center justify-center h-5">
                <Loading relativeSizing startColour="neutral-100" finalColour="neutral-300" />
              </div>
            ) : buttonState === SaveButtonState.SAVED ? (
              savedCopy
            ) : (
              saveCopy
            )}
          </Button>

          {isExpandable && (
            <Popover.Trigger asChild>
              <Button
                disabled={isDisabled}
                size="md"
                onClick={(event) => {
                  event.stopPropagation();
                  return onExpand;
                }}
                data-testid="select-stage-button"
                className={cn({
                  "px-2! pr-3! rounded-l-none!": isExpandable,
                })}
              >
                <i className="flex h-5 text-xl text-white icon-lt-arrow-down-s-line" />
              </Button>
            </Popover.Trigger>
          )}
        </div>
      </Tooltip.Trigger>

      {buttonState === SaveButtonState.ERROR && (
        <Tooltip.Content sideOffset={0} showArrow={true} theme={Theme.Dark} align="start" side="bottom">
          {t("sites.save_site.error_body")}
        </Tooltip.Content>
      )}
    </Tooltip.Wrapper>
  );
}
